import React, { useState } from 'react';
import ImageUpload from './ImageUpload';
import EditableText from './EditableText';
import { ChevronDown, ChevronRight, Plus, Trash2, ChevronUp, ArrowDown } from 'lucide-react';
import api from '../../api/client';
import { toast } from 'sonner';

// Flatten the category tree into an ordered list with depth annotations (DFS from roots)
const buildFlatList = (categories, collapsedRoots) => {
  const roots = categories.filter(c => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
  const list = [];
  const walk = (node, depth) => {
    const subs = categories.filter(c => c.parent_id === node.id).sort((a, b) => (a.order || 0) - (b.order || 0));
    list.push({ cat: node, depth, subsCount: subs.length });
    if (depth === 0 && collapsedRoots.has(node.id)) return; // collapsed root: skip children
    subs.forEach(s => walk(s, depth + 1));
  };
  roots.forEach(r => walk(r, 0));
  return list;
};

const CategoryRow = ({ cat, depth, subsCount, collapsed, onToggle, onUpdate, onDelete, onAddSub, onReorder, canMoveUp, canMoveDown }) => {
  const isRoot = depth === 0;
  return (
    <div className={isRoot ? 'bg-white border border-neutral-200 rounded-sm' : 'bg-orange-50/30'} style={{ marginLeft: depth > 0 ? `${depth * 24}px` : 0 }}>
      <div className={`${isRoot ? 'p-4' : 'p-2 border-l-2 border-orange-300'} grid grid-cols-1 md:grid-cols-[100px_1fr_auto] gap-3 items-start`}>
        <ImageUpload src={cat.image} alt={cat.name} onChange={(u) => onUpdate(cat.id, { image: u })} className="w-[100px] h-[100px]" />
        <div className="space-y-1 text-sm min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isRoot && subsCount > 0 && (
              <button onClick={() => onToggle(cat.id)} className="text-neutral-500 hover:text-[#F46A25]" aria-label={collapsed ? 'Expand' : 'Collapse'}>
                {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">
              {isRoot ? 'Category' : `Sub-category (level ${depth})`}
            </span>
            {subsCount > 0 && <span className="text-[10px] bg-orange-100 text-[#F46A25] font-bold px-1.5 py-0.5 rounded">{subsCount} sub</span>}
          </div>
          <EditableText value={cat.name} onSave={(v) => onUpdate(cat.id, { name: v })} className={`${isRoot ? 'font-bold text-lg' : 'font-semibold'} text-[#12263A]`} />
          <div>
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">Slug (URL)</span>
            <EditableText value={cat.slug} onSave={(v) => onUpdate(cat.id, { slug: v })} className="text-neutral-600 font-mono text-xs" />
          </div>
          <div className="md:col-span-2">
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">SEO intro (100-150 words above the product grid)</span>
            <EditableText
              value={cat.seo_intro || ''}
              placeholder="Write a keyword-rich intro paragraph — this appears on the category landing page and helps Google understand what you sell."
              onSave={(v) => onUpdate(cat.id, { seo_intro: v })}
              multiline
              className="text-neutral-700 text-xs leading-relaxed"
              data-testid={`cat-seo-intro-${cat.id}`}
            />
          </div>
        </div>
        <div className="flex md:flex-col gap-2">
          <div className="flex gap-1">
            <button
              onClick={() => onReorder(cat.id, 'up')}
              disabled={!canMoveUp}
              data-testid={`cat-move-up-${cat.id}`}
              aria-label="Move up"
              className={`text-xs border border-neutral-300 font-semibold px-2 py-1.5 rounded-sm flex items-center ${canMoveUp ? 'hover:border-[#F46A25] hover:text-[#F46A25]' : 'opacity-40 cursor-not-allowed'}`}
            >
              <ChevronUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onReorder(cat.id, 'down')}
              disabled={!canMoveDown}
              data-testid={`cat-move-down-${cat.id}`}
              aria-label="Move down"
              className={`text-xs border border-neutral-300 font-semibold px-2 py-1.5 rounded-sm flex items-center ${canMoveDown ? 'hover:border-[#F46A25] hover:text-[#F46A25]' : 'opacity-40 cursor-not-allowed'}`}
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          </div>
          <button onClick={() => onAddSub(cat.id)} className="text-xs bg-orange-100 hover:bg-orange-200 text-[#F46A25] font-bold px-3 py-1.5 rounded-sm flex items-center gap-1 whitespace-nowrap">
            <Plus className="w-3 h-3" /> Sub
          </button>
          <button onClick={() => onDelete(cat.id)} className="text-xs text-red-600 hover:bg-red-50 border border-red-200 font-semibold px-3 py-1.5 rounded-sm flex items-center gap-1 whitespace-nowrap">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoryTree = ({ categories, onUpdate, onDelete, onAddSub, onReload }) => {
  const [collapsedRoots, setCollapsedRoots] = useState(new Set());
  const toggle = (id) => {
    setCollapsedRoots(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const reorder = async (cid, direction) => {
    try {
      await api.put(`/admin/categories/${cid}/reorder?direction=${direction}`);
      onReload?.();
    } catch (e) { toast.error('Reorder failed'); }
  };
  const flat = buildFlatList(categories, collapsedRoots);
  // Build peer-index lookup: for each category, its position among same-parent peers.
  const peerPositions = new Map(); // catId -> {idx, size}
  const peerGroups = new Map();    // parent_id (or 'root') -> ordered ids
  categories.forEach(c => {
    const key = c.parent_id || 'root';
    const arr = peerGroups.get(key) || [];
    arr.push(c);
    peerGroups.set(key, arr);
  });
  peerGroups.forEach((arr) => {
    arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    arr.forEach((c, idx) => peerPositions.set(c.id, { idx, size: arr.length }));
  });
  if (flat.length === 0) {
    return (
      <div className="bg-white border border-neutral-200 rounded-sm p-10 text-center text-neutral-500">
        No categories yet. Click &quot;Add category&quot; to create your first one.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {flat.map(({ cat, depth, subsCount }) => {
        const pos = peerPositions.get(cat.id) || { idx: 0, size: 1 };
        return (
          <CategoryRow
            key={cat.id}
            cat={cat}
            depth={depth}
            subsCount={subsCount}
            collapsed={collapsedRoots.has(cat.id)}
            onToggle={toggle}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onAddSub={onAddSub}
            onReorder={reorder}
            canMoveUp={pos.idx > 0}
            canMoveDown={pos.idx < pos.size - 1}
          />
        );
      })}
    </div>
  );
};

export default CategoryTree;
